import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Download, KeyRound, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function DownloadPhotosDialog({ open, onClose, slug }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const close = () => { setPasscode(""); setError(""); onClose(); };

  const submit = async (code) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/public/listing/${slug}/download-zip`, { passcode: code }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      close();
    } catch (e) {
      let detail = "That passcode isn't correct — check the email and try again.";
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* keep default */ }
      }
      setError(detail);
      setPasscode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="download-passcode-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><KeyRound size={20} className="text-[#C7A361]" />Enter Passcode</DialogTitle>
          <DialogDescription>Enter the 6-digit passcode from the listing email to download every photo as a zip file.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          <InputOTP data-testid="download-passcode-input" maxLength={6} value={passcode} onChange={(v) => { setPasscode(v); setError(""); if (v.length === 6) submit(v); }} disabled={loading}>
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
          </InputOTP>
          {loading && <span className="flex items-center gap-2 text-sm text-[#667085]"><Loader2 size={14} className="animate-spin" />Preparing your download…</span>}
          {error && <p data-testid="download-passcode-error" className="text-sm text-red-600 text-center">{error}</p>}
          <p className="text-xs text-[#94A3B8] flex items-center gap-1.5"><Download size={12} />Downloads automatically once the passcode is correct</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
